// src/pages/Customer/Home.jsx - SUPER OPTIMIZED WITH CAROUSEL & INFINITE SCROLL + STOCK MANAGEMENT

import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  ShoppingCart,
  Plus,
  ChefHat,
  Star,
  Search,
  Filter,
  MapPin,
  Loader,
  X,
  ChevronLeft,
  ChevronRight,
  Flame,
  PackageX
} from "lucide-react";

import RatingPrompt from "../../components/RatingPrompt";

import { motion, AnimatePresence } from "framer-motion";
import StorageService from "../../services/StorageService";
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../../services/firebaseConfig';
import Cart from "./Cart";
import OrderTracking from "./OrderTracking";
import ActiveOrders from "./ActiveOrders";
import InventoryDeductionService from '../../services/InventoryDeductionService';
const ITEMS_PER_PAGE = 10;
const SESSION_CLOSE_DELAY_MS = 5 * 60 * 1000; // 5 minutes


const CustomerHome = ({ restaurantId, tableId }) => {
  const [restaurant, setRestaurant] = useState(null);
  const [tableName, setTableName] = useState('');
  const [cart, setCart] = useState([]);
  const [showRatingPrompt, setShowRatingPrompt] = useState(false);
  const [ratedOrderId, setRatedOrderId] = useState(null);

  const [view, setView] = useState("menu");
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [subcategoryFilter, setSubcategoryFilter] = useState("All");
  const [showFilters, setShowFilters] = useState(false);
  const [featuredItems, setFeaturedItems] = useState([]);
  const [currentFeaturedIndex, setCurrentFeaturedIndex] = useState(0);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [activeOrders, setActiveOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [portionModal, setPortionModal] = useState({ show: false, item: null });
  
  // ✅ INFINITE SCROLL STATE
  const [displayedItems, setDisplayedItems] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const observerRef = useRef(null);
  const loadMoreRef = useRef(null);


 // 🟢 ADDED: State to hold ALL orders (including Delivered) for the rating check
  const [allTableOrders, setAllTableOrders] = useState([]); 
  
  // 🟢 ADDED: State to manage session closure timer
  const [sessionCloseTimer, setSessionCloseTimer] = useState(null); 
  // 🟢 ADDED: State to indicate session is manually closed/ended
  const [isSessionEnded, setIsSessionEnded] = useState(false); 



  useEffect(() => {
    loadRestaurantData();
  }, [restaurantId, tableId]);

  // ✅ AUTO SLIDE CAROUSEL
  useEffect(() => {
    if (featuredItems.length > 1) {
      const interval = setInterval(() => {
        setCurrentFeaturedIndex(prev => 
          prev === featuredItems.length - 1 ? 0 : prev + 1
        );
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [featuredItems.length]);

  // ✅ OPTIMIZED: Smart polling - only when needed
  useEffect(() => {
    if (restaurant && view === "menu") {
      loadActiveOrders();
      const interval = setInterval(loadActiveOrders, 5000);
      return () => clearInterval(interval);
    }
  }, [restaurantId, tableId, restaurant, view]);


  //new
useEffect(() => {
    // 1. Clear any existing timer
    if (sessionCloseTimer) {
      clearTimeout(sessionCloseTimer);
      setSessionCloseTimer(null);
    }

    // Check for active orders
    const hasActiveOrders = allTableOrders.some(
      o => o.status !== 'Delivered'
    );
    if (hasActiveOrders) {
      // Session remains open if there are active orders
      return;
    }

    const deliveredOrders = allTableOrders.filter(o => o.status === 'Delivered');
    if (deliveredOrders.length === 0) return;

    const latestOrder = deliveredOrders.sort((a, b) => 
      new Date(b.completedAt || b.time).getTime() - new Date(a.completedAt || a.time).getTime()
    )[0];
    
    const hasRated = localStorage.getItem(`rated_${restaurantId}_${latestOrder.id}`);

    // CRITICAL LOGIC: If the rating prompt is currently shown OR the order has been rated, 
    // we do NOT start the session closure timer.
    if (hasRated || (showRatingPrompt && ratedOrderId === latestOrder.id)) {
        console.log('✅ Order rated or Rating Prompt is active. Session closure timer paused.');
        return;
    }

    // 3. Start 5-min closure timer
    console.log('🚨 All orders delivered, unrated, and prompt is closed/expired. Starting 5-min session closure timer.');
      
    const timer = setTimeout(() => {
      console.log('⌛ 5 minutes passed. Closing session.');
      setIsSessionEnded(true);
    }, SESSION_CLOSE_DELAY_MS);

    setSessionCloseTimer(timer);

    // Cleanup on component unmount or dependency change
    return () => {
      // Ensure timer clears on unmount
      if (sessionCloseTimer) {
        clearTimeout(sessionCloseTimer);
      }
    };
}, [allTableOrders, restaurantId, showRatingPrompt, ratedOrderId]);
 

  // ✅ MEMOIZED: Filter menu items only when dependencies change
  // ✅ NOW FILTERS OUT OUT-OF-STOCK ITEMS BY DEFAULT
  const filteredMenu = useMemo(() => {
    if (!restaurant?.menu) return [];
    
    return restaurant.menu.filter((item) => {
      // ✅ Only show in-stock items
      const isInStock = item.inStock !== false; // Default to true if not specified
      
      const matchesSearch = item.name
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesCategory =
        categoryFilter === "All" || item.category === categoryFilter;
      const matchesSubcategory =
        subcategoryFilter === "All" || item.subcategory === subcategoryFilter;
      
      return isInStock && matchesSearch && matchesCategory && matchesSubcategory;
    });
  }, [restaurant?.menu, searchTerm, categoryFilter, subcategoryFilter]);

  const loadRestaurantData = async () => {
    try {
      setLoading(true);
      const rest = await StorageService.getRestaurant(restaurantId);
      
      if (rest && rest.status === "Active") {
        setRestaurant(rest);

        if (tableId) {
          const table = rest.tables?.find(t => t.id === tableId);
          setTableName(table?.name || 'Unknown Table');
        }

        // ✅ Load featured items (foods of the day) - ONLY in-stock items
        const inStockItems = rest.menu.filter(item => item.inStock !== false);
        const featured = inStockItems.filter(item => 
          rest.featuredItems?.includes(item.id)
        );
        setFeaturedItems(featured.length > 0 ? featured : inStockItems.slice(0, 3));

        await loadActiveOrders();
      }
    } catch (error) {
      console.error('❌ Error loading restaurant:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadActiveOrders = async () => {
  try {
    const rest = await StorageService.getRestaurant(restaurantId);
    
    if (rest && tableId) {
      // 🟢 FIX: Fetch ALL table orders first
      const allOrders = rest.orders.filter(
        order => order.tableId === tableId && !order.cancelled
      );
      
      // 🟢 Update the allTableOrders state for the rating check AND session timer check
      setAllTableOrders(allOrders); 

      // 🛑 Original business logic (only non-Delivered orders for 'ActiveOrders' display)
      const activeOrdersForDisplay = allOrders.filter(
        order => order.status !== 'Delivered'
      );
      setActiveOrders(activeOrdersForDisplay);
    }
  } catch (error) {
    console.error('❌ Error loading active orders:', error);
  }
};


  // ✅ CHECK IF ITEM HAS BOTH PORTIONS
  const hasBothPortions = useCallback((item) => {
    return item.portionOptions?.includes('Half') && 
           item.portionOptions?.includes('Full') &&
           item.priceHalf && 
           item.priceFull;
  }, []);

  const handleAddToCartClick = useCallback((item) => {
    if (hasBothPortions(item)) {
      setPortionModal({ show: true, item });
    } else {
      addToCartDirect(item);
    }
  }, [hasBothPortions]);

  const addToCartWithPortion = useCallback((item, portion) => {
    const price = portion === 'Half' ? item.priceHalf : item.priceFull;
    const itemWithPortion = {
      ...item,
      id: `${item.id}_${portion}`,
      name: `${item.name} (${portion})`,
      price: price,
      selectedPortion: portion
    };
    
    setCart(prevCart => {
      const existingItem = prevCart.find((c) => c.id === itemWithPortion.id);
      if (existingItem) {
        return prevCart.map((c) =>
          c.id === itemWithPortion.id ? { ...c, quantity: c.quantity + 1 } : c
        );
      } else {
        return [...prevCart, { ...itemWithPortion, quantity: 1 }];
      }
    });
    
    setPortionModal({ show: false, item: null });
  }, []);



const handleCloseRating = useCallback(() => {
  setShowRatingPrompt(false);
  
  if (ratedOrderId) {
    localStorage.setItem(`rated_${restaurantId}_${ratedOrderId}`, 'true');
    
    // ✅ Use functional update to avoid dependency
    setSessionCloseTimer(prev => {
      if (prev) {
        clearTimeout(prev);
      }
      return null;
    });
  }
}, [restaurantId, ratedOrderId]); // ✅ REMOVED sessionCloseTimer



  const addToCartDirect = useCallback((item) => {
    const validPrice = item.priceFull || item.priceHalf || item.price || 0;

    setCart(prevCart => {
      const existingItem = prevCart.find((c) => c.id === item.id);
      if (existingItem) {
        return prevCart.map((c) =>
          c.id === item.id ? { ...c, quantity: c.quantity + 1 } : c
        );
      } else {
        return [...prevCart, { ...item, price: validPrice, quantity: 1 }];
      }
    });
  }, []);

  const updateQuantity = useCallback((itemId, delta) => {
    setCart(prevCart =>
      prevCart
        .map((c) => {
          if (c.id === itemId) {
            const newQty = c.quantity + delta;
            return newQty > 0 ? { ...c, quantity: newQty } : null;
          }
          return c;
        })
        .filter(Boolean)
    );
  }, []);

  const placeOrder = async (customerName,specialInstructions = '') => {
    if (cart.length === 0) return null;

    try {
      const orderId = `order_${Date.now()}`;
      const order = {
        id: orderId,
        items: cart.map((c) => c.id),
        itemDetails: cart.map((c) => ({
  name: c.name,
  price: c.price,
  quantity: c.quantity,
  category: c.category,        // ✅ ADDED
  subcategory: c.subcategory,  // ✅ ADDED
  portion: c.selectedPortion || 'Full'
  // ✅ ADDED for Half/Full tracking
})),
        total: cart.reduce((sum, c) => sum + c.price * c.quantity, 0),
        status: "Pending",
        time: new Date().toISOString(),
        customerName,
        specialInstructions: specialInstructions || '', // ✅ ADD SPECIAL INSTRUCTIONS
        tableId: tableId || null,
        tableName: tableName || 'Walk-in',
        preparationTime: null,
        preparationStartTime: null
      };

      setActiveOrders(prev => [...prev, order]);
      setCart([]);

      const orderRef = doc(db, 'restaurants', restaurantId, 'orders', orderId);
      await setDoc(orderRef, order);
// ✅ ADD THIS ENTIRE BLOCK:
try {
  console.log('📦 Deducting inventory for order:', orderId);
  const deductionResult = await InventoryDeductionService.deductInventoryForOrder(
    restaurantId,
    cart.map(item => ({
      itemId: item.id.includes('_Half') || item.id.includes('_Full') 
        ? item.id.split('_')[0] + '_' + item.id.split('_')[1]
        : item.id,
      quantity: item.quantity,
      portion: item.selectedPortion || 'Full'
    })),
    orderId
  );
  
  if (deductionResult.success) {
    console.log('✅ Inventory deducted successfully');
  } else {
    console.warn('⚠️ Inventory deduction failed:', deductionResult.error);
  }
} catch (error) {
  console.error('❌ Error in inventory deduction:', error);
}

const sessionId = await StorageService.createSession(orderId, restaurantId, tableId);

      //const sessionId = await StorageService.createSession(orderId, restaurantId, tableId);
      StorageService.clearCache();
      
       
      
      return { orderId, sessionId };
    } catch (error) {
      console.error('❌ Error placing order:', error);
      alert('Failed to place order. Please try again.');
      await loadActiveOrders();
      return null;
    }
  };

  const handleOrderPlaced = useCallback((sessionId) => {
    setCurrentSessionId(sessionId);
    setView("tracking");
  }, []);

  const handleBackToMenu = useCallback(() => {
    setView("menu");
    setCurrentSessionId(null);
    loadActiveOrders();
  }, []);

  const handleViewActiveOrders = useCallback(() => {
    setView("activeOrders");
  }, []);

  // ✅ LOAD MORE ITEMS - NOW DEFINED AFTER filteredMenu
  const loadMoreItems = useCallback(() => {
    if (loadingMore || !hasMore) {
       
      return;
    }

    console.log('📥 Loading more items...', {
      currentLength: displayedItems.length,
      totalFiltered: filteredMenu.length
    });

    setLoadingMore(true);
    
    setTimeout(() => {
      const currentLength = displayedItems.length;
      const nextItems = filteredMenu.slice(currentLength, currentLength + ITEMS_PER_PAGE);
      
       
      
      if (nextItems.length === 0) {
         
        setHasMore(false);
      } else {
        setDisplayedItems(prev => [...prev, ...nextItems]);
        
        if (currentLength + nextItems.length >= filteredMenu.length) {
           
          setHasMore(false);
        }
      }
      
      setLoadingMore(false);
    }, 300);
  }, [displayedItems.length, filteredMenu, loadingMore, hasMore]);

  // ✅ RESET DISPLAYED ITEMS WHEN FILTERS CHANGE
  useEffect(() => {
    // console.log('🔄 Resetting displayed items', {
    //   totalFiltered: filteredMenu.length,
    //   initialLoad: ITEMS_PER_PAGE
    // });
    
    setDisplayedItems(filteredMenu.slice(0, ITEMS_PER_PAGE));
    setHasMore(filteredMenu.length > ITEMS_PER_PAGE);
    setLoadingMore(false);
  }, [filteredMenu]);

  // ✅ INTERSECTION OBSERVER FOR INFINITE SCROLL
  useEffect(() => {
    const currentObserver = observerRef.current;
    
    if (currentObserver) {
      currentObserver.disconnect();
    }

    if (loadingMore || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
           
          loadMoreItems();
        }
      },
      { 
        threshold: 0.1,
        rootMargin: '100px'
      }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
      observerRef.current = observer;
    }

    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, [hasMore, loadingMore, loadMoreItems]);

useEffect(() => {
  let timer;

  const checkForDeliveredOrders = () => {
    // 1. Suppress if session is explicitly ended
    if (isSessionEnded) {
      console.log('❌ Session ended, suppressing rating prompt.');
      return;
    }

    if (!allTableOrders || allTableOrders.length === 0) return;
    
    const deliveredOrders = allTableOrders.filter(o => o.status === 'Delivered');
    if (deliveredOrders.length === 0) return;

    const latestOrder = deliveredOrders.sort((a, b) => 
      new Date(b.time).getTime() - new Date(a.time).getTime()
    )[0];
    
    // 2. Check Global Rating Status (Order ID based)
    const hasRatedGlobally = localStorage.getItem(`rated_${restaurantId}_${latestOrder.id}`);
    if (hasRatedGlobally || !restaurant?.googlePlaceId) return;

    // 3. Check Local Session Prompt Status (Prevent showing multiple times to different users instantly)
    // If this key exists, it means the prompt has been shown to *someone* in this browser session.
    const promptShownToSession = sessionStorage.getItem(`prompted_session_${latestOrder.id}`);
    
    if (promptShownToSession) {
        console.log('⚠️ Rating prompt already shown in this specific session. Suppressing.');
        return;
    }
    
    // 4. Set the order to be rated and show the prompt instantly
    setRatedOrderId(latestOrder.id);
    setShowRatingPrompt(true);
    
    // 5. Set local flag for this browser session
    sessionStorage.setItem(`prompted_session_${latestOrder.id}`, 'true');
    
    // 6. Start a timer to auto-close the prompt after 5 minutes 
    // This addresses the "must be valid for 5 min only" requirement for the rating prompt itself.
    timer = setTimeout(() => {
        console.log(`⌛ 5 minutes passed for order ${latestOrder.id}. Auto-closing rating prompt.`);
        setShowRatingPrompt(false);
        setRatedOrderId(null);
    }, SESSION_CLOSE_DELAY_MS); 
  };
  
  checkForDeliveredOrders();

  // Cleanup: Clear the auto-close timer if component unmounts or dependencies change
  return () => {
      if (timer) {
          clearTimeout(timer);
      }
  };
}, [allTableOrders, restaurantId, restaurant?.googlePlaceId, isSessionEnded]);


  const categories = useMemo(() => {
    if (!restaurant?.menu) return ["All"];
    return ["All", ...new Set(restaurant.menu.map((i) => i.category))];
  }, [restaurant?.menu]);

  const subcategories = useMemo(() => {
    if (!restaurant?.menu) return ["All"];
    return ["All", ...new Set(restaurant.menu.map((i) => i.subcategory))];
  }, [restaurant?.menu]);

  const cartTotal = useMemo(() => {
    return cart.reduce((sum, c) => sum + c.quantity, 0);
  }, [cart]);

  const isInCart = useCallback((itemId) => {
    return cart.some(c => c.id === itemId || c.id.startsWith(`${itemId}_`));
  }, [cart]);

  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleCategoryChange = useCallback((e) => {
    setCategoryFilter(e.target.value);
  }, []);

  const handleSubcategoryChange = useCallback((e) => {
    setSubcategoryFilter(e.target.value);
  }, []);

  const toggleFilters = useCallback(() => {
    setShowFilters(prev => !prev);
  }, []);

  const closePortionModal = useCallback(() => {
    setPortionModal({ show: false, item: null });
  }, []);

  const goToNextFeatured = useCallback(() => {
    setCurrentFeaturedIndex(prev => 
      prev === featuredItems.length - 1 ? 0 : prev + 1
    );
  }, [featuredItems.length]);

  const goToPrevFeatured = useCallback(() => {
    setCurrentFeaturedIndex(prev => 
      prev === 0 ? featuredItems.length - 1 : prev - 1
    );
  }, [featuredItems.length]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-yellow-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-16 h-16 text-orange-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 text-xl">Loading menu...</p>
        </div>
      </div>
    );
  }
   // 🟢 NEW: Session End Display
  if (isSessionEnded) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="text-center p-8 bg-white rounded-xl shadow-lg max-w-md w-full">
          <PackageX className="w-16 h-16 text-orange-500 mx-auto mb-4" />
          <p className="text-2xl font-bold text-gray-800 mb-2">Session Timed Out</p>
          <p className="text-gray-600 mb-6">
            Your session has expired after 5 minutes of inactivity. 
            Would you like to continue browsing our menu?
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => {
                setIsSessionEnded(false);
                setAllTableOrders([]);
              }}
              className="flex-1 bg-orange-500 text-white px-6 py-3 rounded-lg hover:bg-orange-600 font-semibold transition"
            >
              Continue Browsing
            </button>
            <button
              onClick={() => window.location.reload()}
              className="flex-1 bg-gray-200 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-300 font-semibold transition"
            >
              Refresh Page
            </button>
          </div>
        </div>
      </div>
    );
  }


  if (!restaurant) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-gray-600 mb-4">Restaurant not found or inactive</p>
          <button
            onClick={loadRestaurantData}
            className="bg-orange-500 text-white px-6 py-3 rounded-lg hover:bg-orange-600"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (view === "tracking" && currentSessionId) {
    return (
      <OrderTracking
        sessionId={currentSessionId}
        onBackToMenu={handleBackToMenu}
      />
    );
  }

  if (view === "activeOrders") {
    return (
      <ActiveOrders
        restaurantId={restaurantId}
        tableId={tableId}
        onBack={handleBackToMenu}
      />
    );
  }

  if (view === "cart") {
    return (
      <Cart
        cart={cart}
        updateQuantity={updateQuantity}
        placeOrder={placeOrder}
        onBack={() => setView("menu")}
        onOrderPlaced={handleOrderPlaced}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-yellow-50">
      {/* HEADER */}
      <div className="relative bg-gradient-to-r from-orange-600 to-red-600 text-white p-6 shadow-md sticky top-0 z-40">
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-3xl font-bold mb-1 flex items-center gap-2">
                <ChefHat className="w-7 h-7" /> {restaurant.name}
              </h1>
              {tableName && (
                <p className="text-orange-100 text-sm flex items-center gap-1">
                  <MapPin className="w-4 h-4" /> {tableName}
                </p>
              )}
              <p className="text-orange-100 text-sm">
                Delicious food delivered to your table
              </p>
            </div>
            <button
              onClick={() => setView("cart")}
              className="bg-white text-orange-600 px-5 py-2 rounded-xl font-semibold hover:bg-orange-50 flex items-center gap-2 shadow transition"
            >
              <ShoppingCart className="w-5 h-5" />
              Cart ({cartTotal})
            </button>
          </div>

          {/* Active Orders Alert */}
          {activeOrders.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white bg-opacity-20 backdrop-blur-sm rounded-xl p-4 flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                <div className="relative">
                  <MapPin className="w-6 h-6 text-white" />
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-yellow-400 text-orange-600 rounded-full flex items-center justify-center text-xs font-bold">
                    {activeOrders.length}
                  </span>
                </div>
                <div>
                  <p className="text-white font-semibold">
                    Your table have {activeOrders.length} active order{activeOrders.length > 1 ? 's' : ''}
                  </p>
                  <p className="text-orange-100 text-sm">Track your orders in real-time</p>
                </div>
              </div>
              <button
                onClick={handleViewActiveOrders}
                className="bg-white text-orange-600 px-4 py-2 rounded-lg font-semibold hover:bg-orange-50 transition"
              >
                Track Orders
              </button>
            </motion.div>
          )}
        </div>
      </div>

      {/* ✅ FEATURED ITEMS CAROUSEL */}
      {featuredItems.length > 0 && (
        <div className="max-w-6xl mx-auto mt-6 px-6">
          <div className="flex items-center gap-2 mb-3">
            <Flame className="w-6 h-6 text-orange-500" />
            <h2 className="text-2xl font-bold text-gray-800">Today's Special</h2>
          </div>
          
          <div className="relative bg-white rounded-2xl shadow-2xl overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentFeaturedIndex}
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -100 }}
                transition={{ duration: 0.5 }}
                className="relative"
              >
                <div className="flex flex-col md:flex-row">
                  {/* Image Section */}
                  <div className="w-full md:w-1/2 h-64 md:h-80 bg-gradient-to-br from-orange-100 to-yellow-100 relative overflow-hidden">
                    {featuredItems[currentFeaturedIndex].images?.[0] ? (
                      <img
                        src={featuredItems[currentFeaturedIndex].images[0]}
                        alt={featuredItems[currentFeaturedIndex].name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <ChefHat className="w-32 h-32 text-orange-300 absolute inset-0 m-auto" />
                    )}
                    <div className="absolute top-4 right-4 bg-yellow-400 text-orange-900 px-3 py-1 rounded-full font-bold text-sm flex items-center gap-1 shadow-lg">
                      <Star className="w-4 h-4 fill-current" />
                      {featuredItems[currentFeaturedIndex].rating}
                    </div>
                  </div>

                  {/* Content Section */}
                  <div className="w-full md:w-1/2 p-6 md:p-8 flex flex-col justify-between pb-16 md:pb-8">
                    <div>
                      <div className="inline-block bg-gradient-to-r from-orange-500 to-red-500 text-white px-3 py-1 rounded-full text-xs font-bold mb-3">
                        CHEF'S SPECIAL
                      </div>
                      <h3 className="text-3xl font-bold text-gray-800 mb-3">
                        {featuredItems[currentFeaturedIndex].name}
                      </h3>
                      <p className="text-gray-600 mb-4 line-clamp-3">
                        {featuredItems[currentFeaturedIndex].description}
                      </p>
                      <div className="flex flex-wrap gap-2 mb-4">
                        <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                          {featuredItems[currentFeaturedIndex].category}
                        </span>
                        <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                          {featuredItems[currentFeaturedIndex].subcategory}
                        </span>
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          {featuredItems[currentFeaturedIndex].priceHalf && 
                           featuredItems[currentFeaturedIndex].portionOptions?.includes('Half') && (
                            <div className="text-sm text-gray-600">Half: ₹{featuredItems[currentFeaturedIndex].priceHalf}</div>
                          )}
                          <div className="text-3xl font-bold text-orange-600">
                            ₹{featuredItems[currentFeaturedIndex].priceFull || 
                               featuredItems[currentFeaturedIndex].price || 0}
                          </div>
                        </div>
                      </div>

                      <button
                        onClick={() => handleAddToCartClick(featuredItems[currentFeaturedIndex])}
                        disabled={isInCart(featuredItems[currentFeaturedIndex].id)}
                        className={`w-full py-3 rounded-xl font-bold flex items-center justify-center gap-2 transition-all transform hover:scale-105 ${
                          isInCart(featuredItems[currentFeaturedIndex].id)
                            ? 'bg-gray-400 text-white cursor-not-allowed'
                            : 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg hover:shadow-xl'
                        }`}
                      >
                        <Plus className="w-5 h-5" />
                        {isInCart(featuredItems[currentFeaturedIndex].id) ? 'Added to Cart' : 'Add to Cart'}
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Navigation Buttons */}
            {featuredItems.length > 1 && (
              <>
                <button
                  onClick={goToPrevFeatured}
                  className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 bg-white bg-opacity-90 hover:bg-opacity-100 p-2 md:p-3 rounded-full shadow-lg transition-all transform hover:scale-110"
                >
                  <ChevronLeft className="w-5 h-5 md:w-6 md:h-6 text-gray-800" />
                </button>
                <button
                  onClick={goToNextFeatured}
                  className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 bg-white bg-opacity-90 hover:bg-opacity-100 p-2 md:p-3 rounded-full shadow-lg transition-all transform hover:scale-110"
                >
                  <ChevronRight className="w-5 h-5 md:w-6 md:h-6 text-gray-800" />
                </button>

                {/* ✅ DOTS INDICATOR (Desktop Only - Bottom of Card) */}
                <div className="hidden md:flex absolute bottom-4 left-1/2 -translate-x-1/2 gap-2">
                  {featuredItems.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentFeaturedIndex(index)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        index === currentFeaturedIndex 
                          ? 'bg-orange-500 w-8' 
                          : 'bg-gray-300 hover:bg-gray-400'
                      }`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Search + Filters */}
      <div className="max-w-6xl mx-auto p-4 bg-white mt-6 rounded-xl shadow-sm flex flex-col sm:flex-row gap-3 items-center justify-between">
        <div className="flex items-center gap-2 flex-1 w-full">
          <Search className="text-gray-500" />
          <input
            type="text"
            placeholder="Search for dishes..."
            className="flex-1 border-none focus:ring-0 outline-none"
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </div>

        <button
          className="flex items-center gap-2 text-orange-600 font-medium"
          onClick={toggleFilters}
        >
          <Filter className="w-5 h-5" /> Filters
        </button>
      </div>

      {/* Filters Dropdown */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="max-w-6xl mx-auto bg-white p-4 rounded-xl mt-2 shadow-sm flex flex-col sm:flex-row gap-4"
          >
            <select
              value={categoryFilter}
              onChange={handleCategoryChange}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
            >
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
            <select
              value={subcategoryFilter}
              onChange={handleSubcategoryChange}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
            >
              {subcategories.map((sub) => (
                <option key={sub} value={sub}>
                  {sub}
                </option>
              ))}
            </select>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ✅ MENU GRID WITH INFINITE SCROLL */}
      <div className="max-w-6xl mx-auto p-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
          <ChefHat className="w-6 h-6 text-orange-500" />
          Our Menu
        </h2>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayedItems.map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(index * 0.05, 0.5) }}
              className="bg-white rounded-2xl shadow-md hover:shadow-xl transition overflow-hidden flex flex-col relative group"
            >
              <div className="h-48 bg-gray-100 flex items-center justify-center overflow-hidden relative">
                {item.images && item.images.length > 0 ? (
                  <img
                    src={item.images[0]}
                    alt={item.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                  />
                ) : (
                  <ChefHat className="w-20 h-20 text-gray-300 opacity-50" />
                )}
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  className="absolute top-2 right-2 bg-white bg-opacity-90 px-2 py-1 rounded-full flex items-center text-xs font-semibold text-yellow-600 shadow-md"
                >
                  <Star className="w-3 h-3 mr-1 fill-current" /> {item.rating}
                </motion.div>
              </div>

              <div className="p-4 flex flex-col flex-1 justify-between">
                <div>
                  <div className="flex justify-between items-start mb-1">
                    <h3 className="text-lg font-bold text-gray-800">
                      {item.name}
                    </h3>
                    <div className="text-right">
                      {item.priceHalf && item.portionOptions?.includes('Half') && (
                        <div className="text-sm text-gray-600">Half: ₹{item.priceHalf}</div>
                      )}
                      <div className="text-orange-500 font-bold text-lg">
                        ₹{item.priceFull || 0}
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm mb-2 line-clamp-2">{item.description}</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                      {item.category}
                    </span>
                    <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">
                      {item.subcategory}
                    </span>
                  </div>
                </div>

                <button
                  onClick={() => handleAddToCartClick(item)}
                  disabled={isInCart(item.id)}
                  className={`mt-3 py-2 rounded-xl font-semibold flex items-center justify-center gap-2 transition transform hover:scale-105 ${
                    isInCart(item.id)
                      ? 'bg-gray-400 text-white cursor-not-allowed'
                      : 'bg-orange-500 hover:bg-orange-600 text-white shadow-md'
                  }`}
                >
                  <Plus className="w-4 h-4" /> {isInCart(item.id) ? 'Added to Cart' : 'Add to Cart'}
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        {/* ✅ LOADING MORE INDICATOR */}
        {hasMore && (
          <div ref={loadMoreRef} className="flex flex-col justify-center items-center py-8 gap-4">
            {loadingMore ? (
              <div className="flex items-center gap-3">
                <Loader className="w-6 h-6 text-orange-500 animate-spin" />
                <span className="text-gray-600">Loading more items...</span>
              </div>
            ) : (
              <>
                <div className="text-gray-400 text-sm">Scroll for more</div>
                <button
                  onClick={loadMoreItems}
                  className="bg-orange-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-orange-600 transition shadow-md flex items-center gap-2"
                >
                  <Plus className="w-5 h-5" />
                  Load More Items
                </button>
              </>
            )}
          </div>
        )}

        {/* ✅ NO MORE ITEMS */}
        {!hasMore && displayedItems.length > 0 && (
          <div className="text-center py-8 text-gray-500">
            <p className="text-sm">You've reached the end of our menu</p>
          </div>
        )}

        {/* ✅ NO ITEMS FOUND */}
        {displayedItems.length === 0 && !loadingMore && (
          <div className="text-center py-12">
            <ChefHat className="w-20 h-20 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No items found</h3>
            <p className="text-gray-500">Try adjusting your filters or search</p>
          </div>
        )}
      </div>

      {/* FLOATING CART BUTTON (Mobile) */}
      {cart.length > 0 && (
        <motion.button
          onClick={() => setView("cart")}
          initial={{ y: 100 }}
          animate={{ y: 0 }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          className="fixed bottom-6 right-6 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-full shadow-2xl p-4 flex items-center gap-2 font-semibold z-50 hover:shadow-3xl transition"
        >
          <ShoppingCart className="w-6 h-6" />
          <span className="bg-white text-orange-600 rounded-full w-8 h-8 flex items-center justify-center font-bold">
            {cartTotal}
          </span>
        </motion.button>
      )}

      {/* PORTION SELECTION MODAL */}
      <AnimatePresence>
        {portionModal.show && portionModal.item && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 backdrop-blur-sm"
            onClick={closePortionModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl"
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-800">Select Portion Size</h3>
                <button
                  onClick={closePortionModal}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                  aria-label="Close"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="mb-4">
                <p className="text-gray-600 font-medium">{portionModal.item.name}</p>
              </div>

              <div className="space-y-3">
                {/* Half Portion Option */}
                {portionModal.item.priceHalf && portionModal.item.portionOptions?.includes('Half') && (
                  <button
                    onClick={() => addToCartWithPortion(portionModal.item, 'Half')}
                    className="w-full bg-gradient-to-r from-orange-100 to-yellow-100 hover:from-orange-200 hover:to-yellow-200 border-2 border-orange-300 rounded-xl p-4 flex justify-between items-center transition-all group"
                  >
                    <div className="text-left">
                      <div className="font-bold text-gray-800 text-lg">Half Portion</div>
                      <div className="text-sm text-gray-600">Perfect for light appetite</div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-orange-600">₹{portionModal.item.priceHalf}</div>
                      <Plus className="w-5 h-5 text-orange-600 ml-auto mt-1 group-hover:scale-110 transition-transform" />
                    </div>
                  </button>
                )}

                {/* Full Portion Option */}
                {portionModal.item.priceFull && portionModal.item.portionOptions?.includes('Full') && (
                  <button
                    onClick={() => addToCartWithPortion(portionModal.item, 'Full')}
                    className="w-full bg-gradient-to-r from-orange-400 to-red-400 hover:from-orange-500 hover:to-red-500 text-white rounded-xl p-4 flex justify-between items-center transition-all shadow-md hover:shadow-lg group"
                  >
                    <div className="text-left">
                      <div className="font-bold text-lg">Full Portion</div>
                      <div className="text-sm text-orange-100">Best value for your meal</div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold">₹{portionModal.item.priceFull}</div>
                      <Plus className="w-5 h-5 ml-auto mt-1 group-hover:scale-110 transition-transform" />
                    </div>
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    {showRatingPrompt && ratedOrderId && restaurant?.googlePlaceId && (
        <RatingPrompt
          restaurantId={restaurantId}
          tableId={tableId}
          orderId={ratedOrderId}
          restaurantName={restaurant.name}
          googlePlaceId={restaurant.googlePlaceId}
          onClose={handleCloseRating}
        />
      )}
    </div>
  );
};

export default CustomerHome;